/**
 * useDefaultFeeSection
 * DefaultFeeSection 컴포넌트의 상태, API 호출, 핸들러를 담당하는 훅
 */
import { useState, useEffect, useCallback } from 'react';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { useUserInfo } from '@/shared/hooks/useCrudOperations';
import { supplierDefaultFeeApi, DefaultFeeDto, SaveDefaultFeeRequest, SaveDefaultFeeResponse } from '../api/supplierDefaultFeeApi';
import { COMMISSION_TYPE_OPTIONS } from '../utils/supplierFeeHelpers';

export interface DefaultFeeRow extends DefaultFeeDto {
    isEdited: boolean;
    isNew: boolean;
    commissionType: string;
}

interface UseDefaultFeeSectionParams {
    selectedSupplierId?: string;
    isReadOnly: boolean;
}

const buildNewFeeRow = (overrides: Partial<DefaultFeeRow> = {}): DefaultFeeRow => ({
    feeCode: '',
    feeName: '',
    feeType: 'S',
    calcType: '1',
    feeAmt: null,
    feeRate: null,
    settlementPeriod: 'WEEKLY',
    startDay: new Date().toISOString().split('T')[0],
    endDay: '9999-12-31',
    useYn: 'Y',
    commissionType: 'PER_CASE_FIXED',
    isEdited: true,
    isNew: true,
    ...overrides,
});

export const useDefaultFeeSection = ({ selectedSupplierId, isReadOnly }: UseDefaultFeeSectionParams) => {
    const { showToast } = useToast();
    const { getUserInfo } = useUserInfo();

    const [loading, setLoading] = useState<boolean>(true);
    const [defaultFees, setDefaultFees] = useState<DefaultFeeRow[]>([]);
    const [showAddForm, setShowAddForm] = useState<boolean>(false);
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [isExpanded, setIsExpanded] = useState<boolean>(false);

    const [showFeeApplyModal, setShowFeeApplyModal] = useState<boolean>(false);
    const [feeApplyDateInfo, setFeeApplyDateInfo] = useState<{
        applyStartDate: string;
        isImmediateApply: boolean;
    } | null>(null);

    const fetchDefaultFees = useCallback(async () => {
        try {
            setLoading(true);
            const userInfo = getUserInfo();
            if (!userInfo) { setLoading(false); return; }

            const targetSupplierId = selectedSupplierId || userInfo.suppId;
            if (!targetSupplierId) {
                showToast('협력사 정보를 찾을 수 없습니다.', { type: ToastType.ERROR });
                setLoading(false);
                return;
            }

            const fees = await supplierDefaultFeeApi.getDefaultFees(targetSupplierId);
            const feeRows: DefaultFeeRow[] = fees.map((fee) => {
                let commissionType = 'PER_CASE_FIXED';
                if (fee.calcType === '1') {
                    commissionType = fee.settlementPeriod === 'DAILY' ? 'PER_DAY_FIXED' : 'PER_CASE_FIXED';
                } else if (fee.calcType === '2') {
                    commissionType = fee.settlementPeriod === 'DAILY' ? 'PER_DAY_RATE' : 'PER_CASE_RATE';
                }
                return { ...fee, commissionType, isEdited: false, isNew: false };
            }).sort((a, b) => (a.endDay > b.endDay ? -1 : a.endDay < b.endDay ? 1 : 0));

            setDefaultFees(feeRows);
            setLoading(false);
        } catch (error: any) {
            if (error?.response?.status === 404) {
                setDefaultFees([]);
                setLoading(false);
                return;
            }
            if (!sessionStorage.getItem('default-fee-error-shown')) {
                showToast('기본 수수료를 불러오는데 실패했습니다.', { type: ToastType.ERROR });
                sessionStorage.setItem('default-fee-error-shown', 'true');
            }
            setDefaultFees([]);
            setLoading(false);
        }
    }, [selectedSupplierId, getUserInfo, showToast]);

    useEffect(() => {
        fetchDefaultFees();
    }, [fetchDefaultFees]);

    const handleCommissionTypeChange = (index: number, value: string) => {
        const updated = [...defaultFees];
        updated[index].commissionType = value;
        updated[index].isEdited = true;

        if (value === 'PER_CASE_FIXED') {
            updated[index].calcType = '1';
            updated[index].settlementPeriod = 'WEEKLY';
            updated[index].feeRate = null;
        } else if (value === 'PER_CASE_RATE') {
            updated[index].calcType = '2';
            updated[index].settlementPeriod = 'WEEKLY';
            updated[index].feeAmt = null;
        } else if (value === 'PER_DAY_FIXED') {
            updated[index].calcType = '1';
            updated[index].settlementPeriod = 'DAILY';
            updated[index].feeRate = null;
        } else if (value === 'PER_DAY_RATE') {
            updated[index].calcType = '2';
            updated[index].settlementPeriod = 'DAILY';
            updated[index].feeAmt = null;
        }
        setDefaultFees(updated);
    };

    const handleAmountChange = (index: number, value: string) => {
        const updated = [...defaultFees];
        if (!updated[index]) return;

        const numValue = value === '' ? null : Number(value);
        const commissionType = updated[index].commissionType;

        if (commissionType === 'PER_CASE_FIXED' || commissionType === 'PER_DAY_FIXED') {
            updated[index].feeAmt = numValue;
            updated[index].feeRate = null;
        } else {
            updated[index].feeRate = numValue;
            updated[index].feeAmt = null;
        }
        updated[index].isEdited = true;
        setDefaultFees(updated);
    };

    const handleSave = async (index: number) => {
        const fee = defaultFees[index];
        const userInfo = getUserInfo();

        if (!userInfo) {
            showToast('사용자 정보를 찾을 수 없습니다.', { type: ToastType.ERROR });
            return;
        }

        const targetSupplierId = selectedSupplierId || userInfo.suppId;
        if (!targetSupplierId) {
            showToast('협력사 정보를 찾을 수 없습니다.', { type: ToastType.ERROR });
            return;
        }

        if (!fee.commissionType) {
            showToast('수수료구분을 선택해주세요.', { type: ToastType.WARNING });
            return;
        }

        const isFixed = fee.commissionType === 'PER_CASE_FIXED' || fee.commissionType === 'PER_DAY_FIXED';

        if (isFixed && (fee.feeAmt === null || fee.feeAmt < 0)) {
            showToast('금액은 0 이상의 값을 입력해주세요.', { type: ToastType.WARNING });
            return;
        }

        if (!isFixed && (fee.feeRate === null || fee.feeRate < 0 || fee.feeRate > 100)) {
            showToast('정률은 0 이상 100 이하의 값을 입력해주세요.', { type: ToastType.WARNING });
            return;
        }

        try {
            const commissionTypeLabel = COMMISSION_TYPE_OPTIONS.find(opt => opt.value === fee.commissionType)?.label || '기본';
            const autoFeeName = fee.feeName && fee.feeName.trim() !== ''
                ? fee.feeName
                : `기본수수료_${commissionTypeLabel}`;

            const request: SaveDefaultFeeRequest = {
                suppId: targetSupplierId,
                feeCode: fee.feeCode || `DEFAULT_${Date.now()}`,
                feeName: autoFeeName,
                calcType: fee.calcType,
                feeAmt: fee.feeAmt,
                feeRate: fee.feeRate,
                settlementPeriod: fee.settlementPeriod,
                userId: userInfo.userId,
            };

            const response: SaveDefaultFeeResponse = await supplierDefaultFeeApi.saveDefaultFee(request);
            showToast('기본 수수료가 저장되었습니다.', { type: ToastType.SUCCESS });
            await fetchDefaultFees();
            setShowAddForm(false);

            setFeeApplyDateInfo({
                applyStartDate: response.applyStartDate,
                isImmediateApply: response.isImmediateApply
            });
            setShowFeeApplyModal(true);
        } catch (error: any) {
            if (error?.response?.status === 404) {
                showToast('기본 수수료 API가 아직 구현되지 않았습니다.', { type: ToastType.WARNING });
                return;
            }
            showToast('기본 수수료 저장에 실패했습니다.', { type: ToastType.ERROR });
        }
    };

    const handleAddNew = () => {
        setDefaultFees(prev => [...prev, buildNewFeeRow({ commissionType: '' })]);
        setShowAddForm(true);
    };

    // 수수료구분 select 변경 (fees 있는 경우/없는 경우 통합)
    const handleCommissionTypeSelectChange = (value: string) => {
        if (defaultFees.length > 0) {
            handleCommissionTypeChange(0, value);
        } else {
            setDefaultFees([buildNewFeeRow({ commissionType: value })]);
            setShowAddForm(true);
            setIsEditMode(true);
        }
    };

    // 금액 input 변경 (fees 있는 경우/없는 경우 통합)
    const handleAmountInputChange = (rawValue: string) => {
        if (defaultFees.length > 0) {
            const isFixed = defaultFees[0].commissionType === 'PER_CASE_FIXED' || defaultFees[0].commissionType === 'PER_DAY_FIXED';
            const value = isFixed ? rawValue.replace(/[^0-9]/g, '') : rawValue.replace(/[^0-9.]/g, '');
            handleAmountChange(0, value);
        } else {
            const value = rawValue.replace(/[^0-9]/g, '');
            setDefaultFees([buildNewFeeRow({ feeAmt: value ? Number(value) : null })]);
            setShowAddForm(true);
            setIsEditMode(true);
        }
    };

    const handleSaveAndExitEditMode = async () => {
        if (defaultFees.length > 0) {
            await handleSave(0);
            setIsEditMode(false);
        } else {
            if (!showAddForm) handleAddNew();
            setTimeout(async () => { await handleSave(0); setIsEditMode(false); }, 0);
        }
    };

    const handleEnterEditMode = () => setIsEditMode(true);

    const handleCancelEdit = () => {
        setIsEditMode(false);
        fetchDefaultFees();
    };

    const toggleExpanded = () => setIsExpanded(prev => !prev);

    const closeFeeApplyModal = () => {
        setShowFeeApplyModal(false);
        setFeeApplyDateInfo(null);
    };

    return {
        loading,
        defaultFees,
        setDefaultFees,
        showAddForm,
        setShowAddForm,
        isEditMode,
        setIsEditMode,
        isExpanded,
        showFeeApplyModal,
        feeApplyDateInfo,
        handleCommissionTypeChange,
        handleAmountChange,
        handleSave,
        handleAddNew,
        handleCommissionTypeSelectChange,
        handleAmountInputChange,
        handleSaveAndExitEditMode,
        handleEnterEditMode,
        handleCancelEdit,
        toggleExpanded,
        closeFeeApplyModal,
        isReadOnly,
    };
};
